import {
    InteractionRequiredAuthError,
    InteractionStatus,
} from "@azure/msal-browser";
import {useMsal} from "@azure/msal-react";
import {useEffect} from "react";
import {Button, Box} from "@mui/material";
import {STORAGE_IS_SSO} from "../../portal/constants/systemConstants";
import {
    validateTokenAuthorities,
    createUnauthorizedMessage,
    debugAuthenticationState
} from "../../utilities/jwtUtils";
import {debugLog, debugWarn} from "../../utilities/debugLogger";

function ProtectedComponent({setToken, rejectLogin}) {
    const {instance, inProgress, accounts} = useMsal();
    useEffect(() => {
        debugLog('SSO', 'Effect start inProgress=' + inProgress + ' accounts=' + accounts.length);
        debugAuthenticationState(instance, 'useEffect start');

        // Only try to get tokens automatically if we're not in the middle of an interaction
        if (inProgress !== InteractionStatus.None) {
            debugLog('SSO', 'Interaction in progress, skipping token acquisition');
            return;
        }

        let activeAccount = instance.getActiveAccount();

        // Debug logging for account selection
        if (accounts.length > 1) {
            console.log(`Multiple accounts available (${accounts.length}):`, accounts.map(acc => acc.username));
            console.log('Active account:', activeAccount?.username);
        }

        // Handle post-redirect scenario: if we have accounts but no active account,
        // this likely means we just completed a login redirect
        if (!activeAccount && accounts.length > 0) {
            debugLog('SSO', 'No active account but accounts available - likely post-redirect');
            console.log('Available accounts:', accounts.map(acc => acc.username));

            // Set the first account as active (this is the account the user just logged in with)
            const accountToUse = accounts[0];
            debugLog('SSO', 'Setting active account to ' + accountToUse.username);
            instance.setActiveAccount(accountToUse);
            activeAccount = accountToUse;
        }

        // If we still don't have an active account, wait for user interaction
        if (!activeAccount) {
            debugLog('SSO', 'No accounts available, waiting for user');
            return;
        }

        if (activeAccount) {
            debugLog('SSO', 'Using active account ' + activeAccount.username);

            const accessTokenRequest = {
                scopes: ["user.read"],
                account: activeAccount,
            };

            instance
                .acquireTokenSilent(accessTokenRequest)
                .then((accessTokenResponse) => {
                    // Prefer access token with fallback to id token
                    let tokenToUse = accessTokenResponse.accessToken;
                    let usedId = false;
                    if (!validateTokenAuthorities(tokenToUse)) {
                        debugWarn('SSO', 'Access token authorities invalid; falling back to id token');
                        tokenToUse = accessTokenResponse.idToken;
                        usedId = true;
                    }
                    if (validateTokenAuthorities(tokenToUse)) {
                        const prev = localStorage.getItem('id_token');
                        if (prev !== tokenToUse) {
                            setToken(tokenToUse); // dispatch action prop
                            localStorage.setItem(STORAGE_IS_SSO, "true");
                            debugLog('SSO', `Token (${usedId ? 'id' : 'access'}) validated + set for ${activeAccount.username}`);
                        } else {
                            debugLog('SSO', 'Token unchanged; skipping setToken');
                        }
                        debugAuthenticationState(instance, 'after successful token set');
                    } else {
                        debugWarn('SSO', 'Both access & id token rejected (no authorities) for ' + activeAccount.username);
                        debugAuthenticationState(instance, 'after token rejection');
                        rejectLogin(createUnauthorizedMessage(tokenToUse));
                    }
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        debugWarn('SSO', 'Interaction required redirect for ' + activeAccount.username);
                        instance.acquireTokenRedirect(accessTokenRequest);
                    } else {
                        debugWarn('SSO', 'Token acquisition error for ' + activeAccount.username, error);
                    }
                });
        }
    }, [instance, accounts, inProgress, setToken, rejectLogin]);
    const handleSSOLogin = () => {
        debugAuthenticationState(instance, 'before regular login');
        debugLog('SSO', 'Manual login button pressed');
        // Clear recent logout suppression so login can proceed
        sessionStorage.removeItem('msal_recent_logout');

        const loginRequest = {
            scopes: ["User.Read"],
            prompt: "select_account", // Show account picker if multiple accounts
            AccessTokenLifetime: "8:00:00"
        };

        debugLog('SSO', 'Initiating loginRedirect (regular)');
        instance.loginRedirect(loginRequest).catch(e => {
            console.log('Login redirect error:', e);
        });
    };

    // const handleDifferentAccountLogin = () => {
    //     debugAuthenticationState(instance, 'before different account login');
    //     sessionStorage.removeItem('msal_recent_logout');
    //
    //     // Clear authentication cache gently before forcing new login
    //     clearAuthenticationCache(instance, false); // gentle clearing
    //
    //     debugAuthenticationState(instance, 'after gentle cache clear');
    //
    //     const loginRequest = {
    //         scopes: ["User.Read"],
    //         prompt: "login", // Force fresh login
    //         loginHint: "", // Clear any existing hints
    //         AccessTokenLifetime: "8:00:00"
    //     };
    //
    //     debugLog('SSO', 'Initiating different-account loginRedirect');
    //     instance.loginRedirect(loginRequest).catch(e => {
    //         console.log('Different account login error:', e);
    //     });
    // };

    return (
        <Box sx={{display: 'flex', gap: 1, flexDirection: 'column'}}>
            <Button
                id="SSO"
                name="SSO"
                variant="contained"
                color="primary"
                onClick={handleSSOLogin}
                size="small"
            >
                Sign in with Azure
            </Button>
            {/* 
            <Button 
                id="SSO-Different"    
                name="SSO-Different"
                variant="outlined"
                color="primary"
                onClick={handleDifferentAccountLogin}
                size="small"
            >
                Use Different Account
            </Button>
            */}
        </Box>
    );
}

export default function ssoButton(setToken, rejectLogin) {
    return (
        <ProtectedComponent setToken={setToken} rejectLogin={rejectLogin}/>
    );
}